/**
 * A report shows the scan sections its clinic was given, and nothing else.
 *
 * The kiosk records on each scan the sections the super admin had enabled for
 * the clinic when it was taken (`scanSections`, lib/scanSections), and builds
 * the report from those alone. This applies the same cut on the way in, so
 * every part of the page — the findings, the measurement tables, the summary,
 * the figures and the 3D drawer — is built from the permitted data only, even
 * if a field from a disabled section is present on the document.
 *
 * A scan with no `scanSections` predates the setting and is shown whole.
 *
 * Kept in step with KinetiQE/src/lib/scanSections.ts limitReportBundle.
 */

import { scanSectionsOf } from '../scanSections';

export function reportSectionsOf(scan) {
  return scanSectionsOf(scan?.scanSections && typeof scan.scanSections === 'object'
    ? { scanSections: scan.scanSections }
    : null);
}

export function limitScanToSections(scan) {
  if (!scan || !scan.scanSections || typeof scan.scanSections !== 'object') return scan;
  const s = reportSectionsOf(scan);
  const out = { ...scan };
  const dropped = new Set();
  const dropUrls = list => (Array.isArray(list) ? list : []).forEach(u => dropped.add(u));

  if (!s.bodyScan) {
    dropUrls(scan.postureImageUrls);
    dropped.add(scan.postureOverlayUrl);
    Object.assign(out, {
      postureData: null, postureViews: [], postureImageUrls: [], postureOverlayUrl: null, visionAnalysis: null,
    });
  }
  if (!s.legAlignment) {
    dropUrls(scan.legImageUrls);
    dropped.add(scan.legOverlayUrl);
    // The foot type is picked on the leg screen.
    Object.assign(out, {
      legData: null, legPoses: [], legImageUrls: [], legOverlayUrl: null, footType: '', footTypeSource: null,
    });
  }
  if (!s.footScan) {
    dropUrls(scan.scanImageUrls);
    // Foot size is measured from the podoscope photo.
    Object.assign(out, { scanImageUrls: [], archVitals: [], archVisionAnalysis: null, footSize: null });
  }
  if (!s.plantar) {
    Object.assign(out, {
      sensorAnalytics: null, balanceTest: null, walkTest: null,
      sensorHeatmapLeftUrl: null, sensorHeatmapRightUrl: null,
    });
  }
  // Foot size measurement is the super admin's to give, apart from the foot scan.
  if (!s.footSize) out.footSize = null;
  if (!s.footScan && !s.plantar) {
    Object.assign(out, { medialArchType: '', archTypeSource: null });
  }

  // Mat frames are the plantar section; podoscope estimates the foot scan.
  if (Array.isArray(scan.heatmaps) && scan.heatmaps.length) {
    out.heatmaps = scan.heatmaps.filter(h => (h?.source === 'sensor' ? s.plantar : s.footScan));
    // The typed list supersedes the flat one, which mixes both kinds and would
    // otherwise be read as sensor frames once the typed list is empty.
    dropUrls(scan.heatmapUrls);
    out.heatmapUrls = [];
  } else if (!s.plantar) {
    dropUrls(scan.heatmapUrls);
    out.heatmapUrls = [];
  }

  if (Array.isArray(scan.imageUrls)) out.imageUrls = scan.imageUrls.filter(u => !dropped.has(u));
  return out;
}
